import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { scheduleService } from '../services/scheduleService';
import { useNotificationStore } from '../store/notificationStore';
import type { ScheduleDto, ScheduleFilters } from '../types/schedule';
import type { ScheduleType } from '../types/schedule';
import { ScheduleTypes } from '../types/schedule';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

/**
 * Schedule list page component.
 */
export const ScheduleListPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [schedules, setSchedules] = useState<ScheduleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<ScheduleFilters>({
    pageNumber: 1,
    pageSize: 10,
    searchTerm: '',
    isActive: undefined,
  });

  useEffect(() => {
    loadSchedules();
  }, [filters.pageNumber, filters.searchTerm, filters.isActive]);

  const loadSchedules = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await scheduleService.getSchedules(filters);
      setSchedules(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load schedules');
      showNotification(err.message || 'Failed to load schedules', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await scheduleService.deleteSchedule(id);
      showNotification('Schedule deleted successfully', 'success');
      loadSchedules();
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete schedule', 'error');
    }
  };

  const handleToggleActive = async (schedule: ScheduleDto) => {
    try {
      if (schedule.isActive) {
        await scheduleService.disableSchedule(schedule.id);
        showNotification('Schedule disabled successfully', 'success');
      } else {
        await scheduleService.enableSchedule(schedule.id);
        showNotification('Schedule enabled successfully', 'success');
      }
      loadSchedules();
    } catch (err: any) {
      showNotification(err.message || 'Failed to update schedule', 'error');
    }
  };

  const formatScheduleType = (type: ScheduleType): string => {
    switch (type) {
      case ScheduleTypes.Cron:
        return 'Cron';
      case ScheduleTypes.Interval:
        return 'Interval';
      case ScheduleTypes.OneTime:
        return 'One-Time';
      default:
        return type;
    }
  };

  const formatNextExecution = (nextExecutionAt?: string): string => {
    if (!nextExecutionAt) return 'N/A';
    try {
      const date = new Date(nextExecutionAt);
      return date.toLocaleString();
    } catch {
      return nextExecutionAt;
    }
  };

  const formatLastExecution = (lastExecutionAt?: string): string => {
    if (!lastExecutionAt) return 'Never';
    try {
      const date = new Date(lastExecutionAt);
      return date.toLocaleString();
    } catch {
      return lastExecutionAt;
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<ScheduleDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (schedule) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {schedule.name}
          </Typography>
          {schedule.description && (
            <Typography variant="caption" color="text.secondary">
              {schedule.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'agentName',
      label: 'Agent',
      minWidth: 150,
    },
    {
      id: 'scheduleType',
      label: 'Type',
      minWidth: 120,
      render: (schedule) => (
        <Chip label={formatScheduleType(schedule.scheduleType)} size="small" />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (schedule) => (
        <Chip
          label={schedule.isActive ? 'Active' : 'Inactive'}
          color={schedule.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'nextExecutionAt',
      label: 'Next Execution',
      minWidth: 180,
      render: (schedule) => formatNextExecution(schedule.nextExecutionAt),
    },
    {
      id: 'lastExecutionAt',
      label: 'Last Execution',
      minWidth: 180,
      render: (schedule) => formatLastExecution(schedule.lastExecutionAt),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 180,
      align: 'right',
      render: (schedule) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => navigate(`/schedules/${schedule.id}`)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => navigate(`/schedules/${schedule.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={schedule.isActive ? 'Disable' : 'Enable'}>
            <IconButton size="small" onClick={() => handleToggleActive(schedule)}>
              {schedule.isActive ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(schedule.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Schedules"
      createButton={{ label: 'Create Schedule', onClick: () => navigate('/schedules/new'), icon: <AddIcon /> }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label="Search schedules"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.isActive === undefined ? 'all' : filters.isActive ? 'active' : 'inactive'}
              label="Status"
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  ...filters,
                  isActive: value === 'all' ? undefined : value === 'active',
                  pageNumber: 1,
                });
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={schedules.length === 0}
      emptyState={{
        title: 'No schedules found',
        description: 'Create a new schedule to get started.',
        action: { label: 'Create Schedule', onClick: () => navigate('/schedules/new') },
      }}
      pagination={{
        page: filters.pageNumber || 1,
        totalPages: Math.ceil(totalCount / (filters.pageSize || 10)),
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={schedules}
        loading={false}
      />
    </PageListLayout>
  );
};
